import { useState, useEffect } from "react"
import { Button } from "@/modern/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog"
import { ScrollArea } from "@/modern/components/ui/scroll-area"
import { DateHelper } from "@bryntum/schedulerpro";
import { asyncTaskResult } from "../../../network/network.jsx";
import { Card } from "@/modern/components/ui/card";
import { Badge } from "@/modern/components/ui/badge";
import { pluralize } from "@/other/utils/otherUtils.jsx";

const ExecutedIncentivesListModal = ({ unseenIncentivesExecutionOfMine, project, onShowPointsClicked, onClose }) => {
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [isDisplay, setIsDisplay] = useState(false);

  const onCloseModal = () => {
    setIsDisplay(false);
    onClose();
  }

  // Fetch async task results and compute filtered records when modal opens
  useEffect(() => {
    const fetchAndFilter = async () => {
      try {
        const asyncTaskIds = new Set(unseenIncentivesExecutionOfMine.map((record) => record.asyncTask));

        if (asyncTaskIds.size > 0) {
          const fetchPromises = [...asyncTaskIds].map(async (asyncTaskId) => {
            try {
              const response = await asyncTaskResult(asyncTaskId);
              return response;
            } catch (error) {
              console.error(`Failed to fetch async task ${asyncTaskId}:`, error);
              return null;
            }
          });

          const results = await Promise.all(fetchPromises);
          const validResults = results.filter(result => result !== null);

          // Filter validResults by points_created > 0, then map to get corresponding records
          const filtered = validResults
            .filter((result) => result.data.points_created > 0)
            .map((result) => project.outcomeIncentiveStore.getById(result.data.incentive_id))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by createdAt descending

          if (filtered.length) {
            setFilteredRecords(filtered);
            setIsDisplay(true);
          } else {
            onCloseModal();
          }
        }
      } catch (error) {
        console.error("Error fetching async task results:", error);
        onCloseModal();
      }
    };

    fetchAndFilter();
  }, [unseenIncentivesExecutionOfMine]);

  const onShowPoints = async () => {

    try {
      const executedIncentiveRecordIds = filteredRecords.map((item) => item.id);

      if (executedIncentiveRecordIds.length > 0) {
        onShowPointsClicked(executedIncentiveRecordIds);
      }
      onCloseModal();

      project.beginBatch();
      unseenIncentivesExecutionOfMine.forEach((record) => {
        record.updateSeenStatus("markSeen");
      });
      project.endBatch();
    } catch (error) {
      console.error("Error processing records:", error);
    }
  }

  return (
    <Dialog open={isDisplay} onOpenChange={onCloseModal}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Executed {pluralize(filteredRecords.length, "Incentive", "s")}</DialogTitle>
          <DialogDescription>
            {
              filteredRecords.length > 1
              ? "Below is a list of all executed incentive records pending your review."
              : "Below is the executed incentive record pending your review."
            }
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] w-full">
          <div className="space-y-swivu-base p-swivu-base">
            {filteredRecords.map((record, idx) => (
              <Card
                key={idx}
                variant="modern"
                size="base"
                className="flex items-center justify-between"
              >
                <div className="flex-1">
                  <h4 className="text-swivu-md mb-1 font-medium">{record.name}</h4>
                  <p className="text-swivu-sm text-muted-foreground m-0">Created On: {DateHelper.format(new Date(record.createdAt), 'MM/DD/YYYY')}</p>
                </div>
                <Badge variant="primary-gradient" size="sm">
                  Points {record.points}
                </Badge>
              </Card>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button onClick={onShowPoints}>
            Show Points
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ExecutedIncentivesListModal;
